import { useEffect, useRef } from "react";

type UseResizeScrollAdjustOptions = {
  bottomThreshold: number;
};

const findScrollParent = (el: HTMLElement | null) => {
  let parent = el?.parentElement ?? null;

  while (parent && parent !== document.body) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
};

export const useResizeScrollAdjust = <T extends HTMLElement>({
  bottomThreshold,
}: UseResizeScrollAdjustOptions) => {
  const targetRef = useRef<T | null>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const prevHeightRef = useRef<number | null>(null);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    scrollParentRef.current = findScrollParent(el);

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const scrollParent = scrollParentRef.current;
        const target = targetRef.current;
        if (!scrollParent || !target) return;

        const rect = target.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        const currentHeight = rect.height;
        const prevHeight = prevHeightRef.current;
        prevHeightRef.current = currentHeight;

        if (prevHeight === null || currentHeight <= prevHeight) return;

        const heightDelta = currentHeight - prevHeight;
        const elPosition = rect.bottom - parentRect.top;

        if (parentRect.height - bottomThreshold <= elPosition) {
          scrollParent.scrollTop += heightDelta;
        } else if (
          rect.top < parentRect.top &&
          parentRect.height - elPosition > bottomThreshold + 1
        ) {
          scrollParent.scrollTop -= heightDelta;
        }
      });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [bottomThreshold]);

  return targetRef;
};
